import type { DocTypeId } from '../types/document'

/**
 * Official websites Samjho's "Ask" feature may read when a document doesn't
 * answer a question. This file is imported by both the app (to show the user
 * which sites are checked) and the Worker (which enforces it on every URL it
 * fetches, including after redirects). Anything not listed here is never read.
 *
 * Rules for adding a site: it must be run by a government body, a regulator,
 * or a public-sector organisation that issues the kind of document it is
 * listed for. No news sites, blogs, forums, or private "helper" portals.
 */

/** Every host under these suffixes is a government site (NIC-administered). */
const TRUSTED_SUFFIXES = ['.gov.in', '.nic.in']

/** Official sites outside .gov.in / .nic.in (regulators, public-sector banks, state power companies, PAN agencies). */
const TRUSTED_DOMAINS = [
  'india.gov.in',
  'mygov.in',
  'rbi.org.in',
  'npci.org.in',
  'licindia.in',
  // PAN is issued through these two agencies on behalf of the Income Tax Department
  'protean-tinpan.com',
  'utiitsl.com',
  // Public-sector banks
  'sbi.co.in',
  'onlinesbi.sbi',
  'bankofbaroda.in',
  'pnbindia.in',
  'canarabank.com',
  'unionbankofindia.co.in',
  'bankofindia.co.in',
  'indianbank.in',
  'centralbankofindia.co.in',
  'iob.in',
  'ucobank.com',
  'bankofmaharashtra.in',
  'punjabandsindbank.co.in',
  'indiapost.gov.in',
  // State power distribution companies whose sites are not under .gov.in
  'mahadiscom.in',
  'uppcl.org',
  'bsesdelhi.com',
  'tatapower-ddl.com',
  'wbsedcl.in',
  'pspcl.in',
  'kseb.in',
  'tsspdcl.in',
  'apspdcl.in',
  'apepdcl.in',
  'dgvcl.com',
  'mgvcl.com',
  'pgvcl.com',
  'ugvcl.com',
  'tnebltd.gov.in',
  'bescom.karnataka.gov.in',
  'cesc.co.in',
  'jbvnl.co.in',
  'nbpdcl.co.in',
  'sbpdcl.co.in',
  'tpcentralodisha.com',
  'apdcl.org',
  'hpseb.in',
  'jkpdd.gov.in'
]

export function isTrustedHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '')
  if (!host || /^[\d.]+$/.test(host) || host.includes(':')) return false
  if (TRUSTED_SUFFIXES.some((suffix) => host.endsWith(suffix))) return true
  return TRUSTED_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`))
}

/** http(s) only, no credentials, no custom ports, and an allowlisted host. */
export function isTrustedUrl(value: string): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
  if (url.username || url.password || url.port) return false
  return isTrustedHost(url.hostname)
}

export function displayHost(value: string): string {
  try {
    return new URL(value).hostname.replace(/^www\./, '')
  } catch {
    return value
  }
}

interface TypeSources {
  /** Domains shown to the user and given to the search provider. */
  domains: string[]
  /** Official pages that were checked to exist; read when no search provider is configured. */
  seeds: string[]
}

const GENERAL: TypeSources = {
  domains: ['india.gov.in', 'services.india.gov.in', 'myscheme.gov.in'],
  seeds: ['https://services.india.gov.in/', 'https://www.myscheme.gov.in/']
}

const CERTIFICATES: TypeSources = {
  domains: ['services.india.gov.in', 'myscheme.gov.in', 'digilocker.gov.in', 'india.gov.in'],
  seeds: ['https://services.india.gov.in/', 'https://www.digilocker.gov.in/']
}

export const SOURCES_BY_TYPE: Record<DocTypeId, TypeSources> = {
  income_certificate: CERTIFICATES,
  caste_certificate: CERTIFICATES,
  domicile_certificate: CERTIFICATES,
  birth_certificate: { domains: ['crsorgi.gov.in', 'digilocker.gov.in', 'services.india.gov.in'], seeds: CERTIFICATES.seeds },
  death_certificate: { domains: ['crsorgi.gov.in', 'digilocker.gov.in', 'services.india.gov.in'], seeds: CERTIFICATES.seeds },
  electricity_bill: { domains: ['powermin.gov.in', 'services.india.gov.in'], seeds: ['https://services.india.gov.in/'] },
  water_bill: GENERAL,
  ration_card: { domains: ['nfsa.gov.in', 'dfpd.gov.in', 'myscheme.gov.in', 'services.india.gov.in'], seeds: ['https://services.india.gov.in/'] },
  aadhaar: { domains: ['uidai.gov.in', 'myaadhaar.uidai.gov.in'], seeds: ['https://uidai.gov.in/en/'] },
  pan_card: {
    domains: ['incometax.gov.in', 'protean-tinpan.com', 'utiitsl.com'],
    seeds: ['https://www.incometax.gov.in/iec/foportal/']
  },
  bank_notice: { domains: ['rbi.org.in', 'cms.rbi.org.in'], seeds: ['https://www.rbi.org.in/commonperson/English/Scripts/FAQs.aspx'] },
  loan_notice: { domains: ['rbi.org.in', 'cms.rbi.org.in', 'nalsa.gov.in'], seeds: ['https://www.rbi.org.in/commonperson/English/Scripts/FAQs.aspx'] },
  income_tax_notice: {
    domains: ['incometax.gov.in'],
    seeds: ['https://www.incometax.gov.in/iec/foportal/help/individual/return-applicable-1']
  },
  pension: {
    domains: ['pensionersportal.gov.in', 'jeevanpramaan.gov.in', 'epfindia.gov.in', 'nsap.nic.in', 'myscheme.gov.in'],
    seeds: ['https://jeevanpramaan.gov.in/']
  },
  court_notice: { domains: ['nalsa.gov.in', 'ecourts.gov.in', 'services.ecourts.gov.in'], seeds: ['https://ecourts.gov.in/ecourts_home/', 'https://nalsa.gov.in/'] },
  land_record: { domains: ['dolr.gov.in', 'services.india.gov.in'], seeds: ['https://dolr.gov.in/'] },
  property_tax: GENERAL,
  scholarship: { domains: ['scholarships.gov.in', 'myscheme.gov.in'], seeds: ['https://scholarships.gov.in/'] },
  farmer_scheme: { domains: ['pmkisan.gov.in', 'pmfby.gov.in', 'agriwelfare.gov.in', 'myscheme.gov.in'], seeds: ['https://pmkisan.gov.in/', 'https://pmfby.gov.in/'] },
  school_letter: { domains: ['education.gov.in', 'cbse.gov.in'], seeds: ['https://www.cbse.gov.in/'] },
  marksheet: { domains: ['cbse.gov.in', 'digilocker.gov.in', 'education.gov.in'], seeds: ['https://www.cbse.gov.in/', 'https://www.digilocker.gov.in/'] },
  insurance: { domains: ['irdai.gov.in', 'bimabharosa.irdai.gov.in', 'licindia.in'], seeds: ['https://bimabharosa.irdai.gov.in/'] },
  traffic_challan: { domains: ['echallan.parivahan.gov.in', 'parivahan.gov.in'], seeds: ['https://echallan.parivahan.gov.in/'] },
  job_notice: { domains: ['ssc.gov.in', 'upsc.gov.in', 'ncs.gov.in'], seeds: ['https://ssc.gov.in/'] },
  application_receipt: GENERAL,
  application_decision: { domains: [...GENERAL.domains, 'pgportal.gov.in'], seeds: GENERAL.seeds },
  government_notice: GENERAL,
  lab_report: { domains: ['mohfw.gov.in', 'icmr.gov.in', 'nhm.gov.in'], seeds: ['https://www.mohfw.gov.in/'] },
  rental_agreement: { domains: ['mohua.gov.in', 'consumerhelpline.gov.in', 'india.gov.in'], seeds: ['https://mohua.gov.in/'] },
  loan_agreement: { domains: ['rbi.org.in', 'cms.rbi.org.in'], seeds: ['https://www.rbi.org.in/commonperson/English/Scripts/FAQs.aspx'] },
  contract: { domains: ['indiacode.nic.in', 'consumerhelpline.gov.in', 'india.gov.in'], seeds: GENERAL.seeds },
  unknown: GENERAL
}

/** Official sources for the legal help page's questions (no document involved). */
export const LEGAL_SOURCES: TypeSources = {
  domains: [
    'nalsa.gov.in',
    'doj.gov.in',
    'ecourts.gov.in',
    'cybercrime.gov.in',
    'consumerhelpline.gov.in',
    'ncw.nic.in',
    'rtionline.gov.in',
    'pgportal.gov.in',
    'indiacode.nic.in',
    'mha.gov.in',
    'wcd.gov.in',
    'cic.gov.in',
    'pib.gov.in'
  ],
  seeds: ['https://nalsa.gov.in/legal-aid/', 'https://nalsa.gov.in/faqs/']
}

export function sourcesFor(type: DocTypeId): TypeSources {
  return SOURCES_BY_TYPE[type] ?? GENERAL
}
